/**
 * WebMCP: the site's tools, offered to an AI agent driving this browser tab.
 *
 * WebMCP (webmachinelearning.github.io/webmcp) lets a page register tools on
 * `document.modelContext` that an agent in the browser can call instead of
 * guessing at the DOM. It is a draft, shipped behind a flag in Chrome and
 * nowhere else, so on every browser a visitor actually uses this registers
 * nothing and costs one property lookup.
 *
 * ── Which tools, and which not ───────────────────────────────────────────────
 *
 * Two, both of them things the page already lets anyone do:
 *
 *   read_fazly_page   any page of the site as markdown, via the same
 *                     negotiation every agent gets over HTTP (`negotiate.ts`)
 *   open_fazly_tool   go to one of the free measurement tools, in the page's
 *                     language
 *
 * NOT a "measure this photo" tool, although the measuring is the site's main
 * action. The tools exist for a person to measure their OWN face, in their
 * own browser, by choosing the photo themselves; a tool that took a photo from
 * an agent would measure whoever's face the agent was handed, which is the one
 * use this site is built to make awkward. The open tool says so in its
 * description, which is the text the agent reads.
 *
 * ── The contract, which is house rule 6 ──────────────────────────────────────
 *
 * Inert without the API, cannot throw, cannot hang. Registration is wrapped at
 * the sync boundary AND its promise is caught — the App Check lesson in
 * CLAUDE.md is that a `.catch` alone misses a synchronous throw. Nothing
 * awaits it, so nothing on the page waits for an agent.
 */

import { documentLang } from "../seo";
import { TOOL_SLUGS, type ToolSlug } from "../tools/types";

/** The parts of the WebMCP surface this file uses, from the spec's IDL. */
interface AgentTool {
  readonly name: string;
  readonly title?: string;
  readonly description: string;
  readonly inputSchema?: object;
  readonly annotations?: { readonly readOnlyHint?: boolean };
  execute(
    input: Record<string, unknown>,
    options?: { readonly signal?: AbortSignal },
  ): Promise<unknown>;
}

interface ModelContext {
  registerTool(
    tool: AgentTool,
    options?: { readonly signal?: AbortSignal },
  ): unknown;
}

/**
 * The page's model context, or null. The spec puts it on `document`; Chrome's
 * first builds put it on `navigator`, and the scanners look in both.
 */
function modelContext(): ModelContext | null {
  try {
    const candidate =
      (document as { modelContext?: ModelContext }).modelContext ??
      (navigator as { modelContext?: ModelContext }).modelContext;
    return typeof candidate?.registerTool === "function" ? candidate : null;
  } catch {
    return null;
  }
}

/**
 * A free tool's path, in a language. The same shape as `toolPath` in
 * `tools/index.ts`, written out because that module imports the English tool
 * copy and this one runs on every page; `webmcp.test.ts` holds the two equal.
 */
export function toolHref(slug: ToolSlug, lang: string): string {
  return lang === "en" ? `/tools/${slug}` : `/${lang}/tools/${slug}`;
}

/** A same-origin path, or null for anything that would leave the site. */
export function sitePath(path: unknown, origin: string): string | null {
  if (typeof path !== "string" || !path.startsWith("/") || path.length > 300)
    return null;
  try {
    const url = new URL(path, origin);
    return url.origin === origin ? url.pathname + url.search : null;
  } catch {
    return null;
  }
}

export function agentTools(navigate: (href: string) => void): AgentTool[] {
  return [
    {
      name: "read_fazly_page",
      title: "Read a Fazly page",
      description:
        "Read a page of fazly.app as Markdown without leaving this one: a glossary term (/glossary/canthal-tilt), a guide (/how-to/fix-negative-canthal-tilt), the scoring method (/methodology), a free tool's page (/tools/face-shape), or an index (/tools, /glossary, /how-to, /compare, /best). /llms.txt lists every page.",
      inputSchema: {
        type: "object",
        properties: {
          path: {
            type: "string",
            description: "A path on fazly.app, starting with /",
          },
        },
        required: ["path"],
        additionalProperties: false,
      },
      annotations: { readOnlyHint: true },
      async execute(input, options) {
        const path = sitePath(input.path, window.location.origin);
        if (!path) throw new Error("path must be a path on fazly.app");
        const response = await fetch(path, {
          headers: { accept: "text/markdown" },
          signal: options?.signal,
        });
        const text = await response.text();
        if (!response.ok)
          throw new Error(`${path} answered ${response.status}`);
        return text;
      },
    },
    {
      name: "open_fazly_tool",
      title: "Open a free face-measurement tool",
      description:
        "Open one of Fazly's free face-measurement tools in this tab, in the page's language. The person then chooses a photo of their own face, which is measured in this browser and never uploaded. Do not supply a photo yourself, and do not use a tool on anyone else's face.",
      inputSchema: {
        type: "object",
        properties: {
          tool: {
            type: "string",
            enum: [...TOOL_SLUGS],
            description:
              "Which measurement: looksmax-test is the composite of all eight, the rest measure one thing each.",
          },
        },
        required: ["tool"],
        additionalProperties: false,
      },
      async execute(input) {
        const tool = TOOL_SLUGS.find((slug) => slug === input.tool);
        if (!tool)
          throw new Error(`tool must be one of ${TOOL_SLUGS.join(", ")}`);
        const href = toolHref(
          tool,
          documentLang(window.location.pathname, window.location.search),
        );
        navigate(href);
        return `Opened ${href}. The person chooses their own photo there; it is measured on this device and nothing is uploaded.`;
      },
    },
  ];
}

/**
 * Register the tools, and return what unregisters them.
 *
 * The spec unregisters on an AbortSignal; Chrome's first builds returned an
 * object with `unregister()` instead. Both are honoured, and a build that does
 * neither keeps its tools until the page goes — which, on a site whose every
 * navigation is client-side, is a duplicate-name rejection on the next mount,
 * caught and ignored below.
 */
export function registerAgentTools(
  navigate: (href: string) => void,
): () => void {
  const context = modelContext();
  if (!context) return () => {};
  const controller = new AbortController();
  const legacy: (() => void)[] = [];
  for (const tool of agentTools(navigate)) {
    try {
      const result = context.registerTool(tool, { signal: controller.signal });
      if (result instanceof Promise) result.catch(() => {});
      const unregister = (result as { unregister?: unknown } | undefined)
        ?.unregister;
      if (typeof unregister === "function")
        legacy.push(() => unregister.call(result));
    } catch {
      // A browser whose draft disagrees with ours registers nothing; the page
      // is exactly what it was without WebMCP.
    }
  }
  return () => {
    controller.abort();
    for (const unregister of legacy) {
      try {
        unregister();
      } catch {
        // See above.
      }
    }
  };
}
